import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Input, Toggle, Loading } from '../../../../common/ui';
import { showAlert } from '../../../../common/actions';
import { fetchIsModifierCompany } from '../../../reports/billHistory/utils/companyType';
import {
  fetchModifierGroups,
  createModifierGroup,
  updateModifierGroup,
  deleteModifierGroup,
  createModifierOption,
  updateModifierOption,
  deleteModifierOption,
} from '../actions';
import styles from './styles.module.css';

const emptyGroup = {
  name: '',
  isRequired: false,
  minSelect: 0,
  maxSelect: '',
  sortOrder: 0,
};

const emptyOption = {
  name: '',
  price: 0,
  unitCost: 0,
  isAvailable: true,
  sortOrder: 0,
};

const Modifiers = ({ auth: { companyId }, showAlert }) => {
  const [modifiersEnabled, setModifiersEnabled] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [optionForm, setOptionForm] = useState(emptyOption);
  const [optionGroupId, setOptionGroupId] = useState(null);
  const [editingOptionId, setEditingOptionId] = useState(null);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchModifierGroups();
      setGroups(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setGroups([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      const enabled = await fetchIsModifierCompany(companyId);
      if (cancelled) return;
      setModifiersEnabled(enabled);
      if (enabled) {
        loadGroups();
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [companyId, loadGroups]);

  const requiredMin = groupForm.isRequired
    ? Math.max(1, Number(groupForm.minSelect) || 0)
    : Math.max(0, Number(groupForm.minSelect) || 0);

  const saveGroup = async (e) => {
    e.preventDefault();
    const payload = {
      name: groupForm.name.trim(),
      isRequired: Boolean(groupForm.isRequired),
      minSelect: requiredMin,
      maxSelect:
        groupForm.maxSelect === '' || groupForm.maxSelect == null
          ? null
          : Number(groupForm.maxSelect),
      sortOrder: Number(groupForm.sortOrder) || 0,
    };
    if (!payload.name) {
      showAlert({
        isAlertOpen: true,
        alertType: 'danger',
        alertMessage: 'Group name is required.',
      });
      return;
    }
    try {
      if (editingGroup) {
        await updateModifierGroup(editingGroup.id, payload);
      } else {
        await createModifierGroup(payload);
      }
      setEditingGroup(null);
      setGroupForm(emptyGroup);
      await loadGroups();
    } catch (error) {
      showAlert({
        isAlertOpen: true,
        alertType: 'danger',
        alertMessage: error?.response?.data?.error || 'Failed to save group.',
      });
    }
  };

  const saveOption = async (e) => {
    e.preventDefault();
    if (!optionGroupId) return;
    const payload = {
      groupId: optionGroupId,
      name: optionForm.name.trim(),
      price: Math.max(0, Number(optionForm.price) || 0),
      unitCost: Math.max(0, Number(optionForm.unitCost) || 0),
      isAvailable: optionForm.isAvailable !== false,
      sortOrder: Number(optionForm.sortOrder) || 0,
    };
    if (!payload.name) {
      showAlert({
        isAlertOpen: true,
        alertType: 'danger',
        alertMessage: 'Option name is required.',
      });
      return;
    }
    try {
      if (editingOptionId) {
        await updateModifierOption(editingOptionId, payload);
      } else {
        await createModifierOption(payload);
      }
      setEditingOptionId(null);
      setOptionForm(emptyOption);
      await loadGroups();
    } catch (error) {
      showAlert({
        isAlertOpen: true,
        alertType: 'danger',
        alertMessage: error?.response?.data?.error || 'Failed to save option.',
      });
    }
  };

  if (modifiersEnabled == null) {
    return (
      <div className={styles.container}>
        <Loading variant="overlay" label="" />
      </div>
    );
  }

  if (modifiersEnabled === false) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.pageTitle}>Modifiers</h1>
            <p className={styles.breadcrumb}>Inventory / Modifiers</p>
          </div>
        </div>
        <p className={styles.emptyState}>
          Product modifiers are only available for restaurant and retail companies.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.pageTitle}>Modifiers</h1>
          <p className={styles.breadcrumb}>Inventory / Modifiers</p>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          {loading && <Loading variant="overlay" label="" />}
          <form onSubmit={saveGroup}>
            <div className={styles.formGrid}>
              <Input
                label="Group name"
                value={groupForm.name}
                onChange={(value) => setGroupForm((prev) => ({ ...prev, name: value }))}
              />
              <Input
                label="Sort order"
                type="number"
                value={groupForm.sortOrder}
                onChange={(value) =>
                  setGroupForm((prev) => ({ ...prev, sortOrder: value }))
                }
              />
              <Input
                label="Min select"
                type="number"
                value={groupForm.minSelect}
                onChange={(value) =>
                  setGroupForm((prev) => ({ ...prev, minSelect: value }))
                }
              />
              <Input
                label="Max select"
                type="number"
                value={groupForm.maxSelect}
                onChange={(value) =>
                  setGroupForm((prev) => ({ ...prev, maxSelect: value }))
                }
              />
            </div>
            <Toggle
              label="Required"
              labelPlacement="start"
              name="isRequired"
              checked={Boolean(groupForm.isRequired)}
              onChange={(e) =>
                setGroupForm((prev) => ({
                  ...prev,
                  isRequired: e.target.checked,
                  minSelect: e.target.checked
                    ? Math.max(1, Number(prev.minSelect) || 1)
                    : prev.minSelect,
                }))
              }
            />
            <div className={styles.buttonRow}>
              <Button type="submit">{editingGroup ? 'Update group' : 'Add group'}</Button>
              {editingGroup && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setEditingGroup(null);
                    setGroupForm(emptyGroup);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </div>

        <div className={styles.card}>
          <div className={styles.toolbar}>
            <strong>Groups</strong>
          </div>
          {groups.length === 0 && (
            <p className={styles.emptyState}>No modifier groups yet.</p>
          )}
          {groups.map((group) => (
            <div key={group.id} className={styles.groupRow}>
              <div className={styles.groupHeader}>
                <div>
                  <div className={styles.groupName}>{group.name}</div>
                  <div className={styles.meta}>
                    {group.isRequired ? 'Required' : 'Optional'} · min {group.minSelect ?? 0}
                    {group.maxSelect != null ? `–${group.maxSelect}` : '+'}
                  </div>
                </div>
                <div className={styles.actions}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setEditingGroup(group);
                      setGroupForm({
                        name: group.name || '',
                        isRequired: Boolean(group.isRequired),
                        minSelect: group.minSelect ?? 0,
                        maxSelect: group.maxSelect ?? '',
                        sortOrder: group.sortOrder ?? 0,
                      });
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={async () => {
                      try {
                        await deleteModifierGroup(group.id);
                        await loadGroups();
                      } catch (error) {
                        showAlert({
                          isAlertOpen: true,
                          alertType: 'danger',
                          alertMessage:
                            error?.response?.data?.error || 'Failed to delete group.',
                        });
                      }
                    }}
                  >
                    Delete
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setOptionGroupId(group.id);
                      setEditingOptionId(null);
                      setOptionForm(emptyOption);
                    }}
                  >
                    Add option
                  </Button>
                </div>
              </div>
              <ul className={styles.optionList}>
                {(group.options || []).map((option) => (
                  <li key={option.id} className={styles.optionRow}>
                    <span>
                      {option.name}
                      {Number(option.price) > 0 ? ` · +${Number(option.price).toFixed(2)}` : ''}
                      {option.isAvailable === false ? ' · unavailable' : ''}
                    </span>
                    <span className={styles.actions}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setOptionGroupId(group.id);
                          setEditingOptionId(option.id);
                          setOptionForm({
                            name: option.name || '',
                            price: option.price ?? 0,
                            unitCost: option.unitCost ?? 0,
                            isAvailable: option.isAvailable !== false,
                            sortOrder: option.sortOrder ?? 0,
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={async () => {
                          try {
                            await deleteModifierOption(option.id);
                            await loadGroups();
                          } catch (error) {
                            showAlert({
                              isAlertOpen: true,
                              alertType: 'danger',
                              alertMessage:
                                error?.response?.data?.error ||
                                'Failed to delete option.',
                            });
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
              {optionGroupId === group.id && (
                <form onSubmit={saveOption}>
                  <div className={styles.formGrid}>
                    <Input
                      label="Option name"
                      value={optionForm.name}
                      onChange={(value) =>
                        setOptionForm((prev) => ({ ...prev, name: value }))
                      }
                    />
                    <Input
                      label="Selling price"
                      type="number"
                      value={optionForm.price}
                      onChange={(value) =>
                        setOptionForm((prev) => ({ ...prev, price: value }))
                      }
                    />
                    <Input
                      label="Unit cost"
                      type="number"
                      value={optionForm.unitCost}
                      onChange={(value) =>
                        setOptionForm((prev) => ({ ...prev, unitCost: value }))
                      }
                    />
                    <Input
                      label="Sort"
                      type="number"
                      value={optionForm.sortOrder}
                      onChange={(value) =>
                        setOptionForm((prev) => ({ ...prev, sortOrder: value }))
                      }
                    />
                  </div>
                  <Toggle
                    label="Available"
                    labelPlacement="start"
                    name="isAvailable"
                    checked={optionForm.isAvailable !== false}
                    onChange={(e) =>
                      setOptionForm((prev) => ({
                        ...prev,
                        isAvailable: e.target.checked,
                      }))
                    }
                  />
                  <div className={styles.buttonRow}>
                    <Button type="submit">
                      {editingOptionId ? 'Update option' : 'Save option'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setOptionGroupId(null);
                        setEditingOptionId(null);
                        setOptionForm(emptyOption);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.authState,
});

export default connect(mapStateToProps, { showAlert })(Modifiers);
